import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, Mail, Building2, ShieldAlert, ArrowRight, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { db } from '@/src/lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { calculateLeadScore, BusinessSize, Urgency, Budget } from '@/src/lib/leadScoring';
import { handleFirestoreError, OperationType } from '@/src/lib/firestore-errors';
import { useNavigate } from 'react-router-dom';
import { trackLead } from '@/src/lib/analytics';

interface FormData {
  name: string;
  email: string;
  businessType: string;
  businessSize: string;
  securityConcern: string;
  urgency: string;
  budget: string;
}

export default function MultiStepLeadForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    businessType: '',
    businessSize: '',
    securityConcern: '',
    urgency: '',
    budget: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const totalSteps = 5;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // 1. Calculate Lead Score
      const { score, category } = calculateLeadScore(
        formData.businessSize as BusinessSize,
        formData.urgency as Urgency,
        formData.budget as Budget
      );

      // 2. Save Lead to CRM (Firestore)
      const leadRef = await addDoc(collection(db, 'leads'), {
        ...formData,
        score,
        category,
        status: 'new',
        createdAt: serverTimestamp(),
      });

      // 3. Simulate Automated Email Sequence
      const emailSequence = [
        { 
          type: 'immediate', 
          subject: `Security Assessment Received - Next Steps for ${formData.name}`, 
          content: `Hello ${formData.name},\n\nThank you for your interest in AGRANDINTL. We've received your security assessment and our intelligence unit is currently analyzing your profile.\n\nNext Steps:\n1. Your initial security score is being calculated.\n2. Expect a preliminary risk report in your inbox within 24-48 hours.\n3. If you have immediate concerns, you can skip the queue by booking a discovery call here: [Link].\n\nStay secure,\nThe AGRANDINTL Team`,
          scheduledAt: new Date().toISOString(),
          status: 'sent',
          sentAt: new Date().toISOString()
        },
        { 
          type: '24h', 
          subject: 'The Hidden Cost of Inaction: Why Cybersecurity is a Business Priority', 
          content: `Hello ${formData.name},\n\nCybersecurity is no longer just an "IT issue"—it's an existential business risk. With the average cost of a data breach reaching $4.45M, the price of inaction is far higher than the cost of defense.\n\nIn our next report, we'll dive deeper into how proactive hardening protects not just your data, but your revenue and brand reputation.\n\nBest regards,\nMarcus Vane\nChief Intelligence Officer, AGRANDINTL`,
          scheduledAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        },
        { 
          type: '3d', 
          subject: 'Case Study: How We Prevented a $2M Ransomware Attack', 
          content: `Hello ${formData.name},\n\nWe recently worked with a global retail group that had a critical, "unfindable" vulnerability in their cloud infrastructure. Our elite red-team simulated a real-world attack and identified the gap before hackers could.\n\nBy patching this single point of failure, we saved them an estimated $2M in potential ransomware costs and weeks of downtime.\n\nYou can read the full breakdown here: [Link to Case Study].\n\nStay vigilant,\nThe AGRANDINTL Team`,
          scheduledAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        },
        { 
          type: '5d', 
          subject: 'Ready to Harden Your Defenses? Let\'s Talk Strategy.', 
          content: `Hello ${formData.name},\n\nYou've seen the risks and you've seen the results we deliver. Now, it's time to build your custom defense roadmap.\n\nI'd like to offer you a confidential 15-minute strategy session to discuss your specific architecture and how we can eliminate your highest-priority risks.\n\nBook your session here: [Link to Calendar].\n\nTo your resilience,\nMarcus Vane\nAGRANDINTL`,
          scheduledAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending'
        }
      ];

      for (const email of emailSequence) {
        await addDoc(collection(db, `leads/${leadRef.id}/emails`), email);
      }

      // Track lead submission
      trackLead({
        business_type: formData.businessType,
        business_size: formData.businessSize,
        urgency: formData.urgency,
        score: score
      });

      setIsSuccess(true);
      setTimeout(() => {
        navigate('/thank-you');
      }, 1500);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'leads');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <AnimatePresence mode="wait">
        {!isSuccess ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass-card rounded-[2.5rem] p-8 md:p-12 border-accent/20 relative overflow-hidden"
          >
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-white/5">
              <motion.div
                className="h-full bg-accent shadow-[0_0_10px_#0096ff]"
                initial={{ width: '0%' }}
                animate={{ width: `${(step / totalSteps) * 100}%` }}
              />
            </div>

            <div className="flex justify-between items-center mb-10">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-accent uppercase tracking-[0.3em]">Step {step} of {totalSteps}</p>
                <h3 className="text-2xl font-display font-bold tracking-tight">
                  {step === 1 && "Personal Information"}
                  {step === 2 && "Business Context"}
                  {step === 3 && "Security Priorities"}
                  {step === 4 && "Project Urgency"}
                  {step === 5 && "Investment Range"}
                </h3>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                <Clock className="w-3.5 h-3.5 text-accent" />
                Takes 2 mins
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Full Name</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          required
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => updateFormData('name', e.target.value)}
                          className="pl-12 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Work Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          required
                          type="email"
                          placeholder="john@enterprise.com"
                          value={formData.email}
                          onChange={(e) => updateFormData('email', e.target.value)}
                          className="pl-12 bg-white/5 border-white/10 h-14 rounded-xl focus:border-accent/50"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Industry Sector</label>
                      <select
                        required
                        value={formData.businessType}
                        onChange={(e) => updateFormData('businessType', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 h-14 rounded-xl px-6 focus:border-accent/50 outline-none text-foreground appearance-none"
                      >
                        <option value="" disabled className="bg-background">Select Industry</option>
                        <option value="finance" className="bg-background">Finance & Banking</option>
                        <option value="healthcare" className="bg-background">Healthcare</option>
                        <option value="retail" className="bg-background">Retail & E-commerce</option>
                        <option value="tech" className="bg-background">Technology / SaaS</option>
                        <option value="manufacturing" className="bg-background">Manufacturing</option>
                      </select>
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Company Size</label>
                      <div className="grid grid-cols-2 gap-4">
                        {['1-50', '51-200', '201-1000', '1000+'].map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => updateFormData('businessSize', size)}
                            className={`h-14 rounded-xl border transition-all font-medium ${
                              formData.businessSize === size
                                ? 'bg-accent border-accent text-background shadow-[0_0_20px_rgba(0,150,255,0.3)]'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            {size} Employees
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Primary Security Concern</label>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { id: 'breach', label: 'Data Breach Prevention', icon: ShieldAlert },
                          { id: 'compliance', label: 'Compliance & Regulatory Audit', icon: CheckCircle2 },
                          { id: 'ransomware', label: 'Ransomware Protection', icon: AlertCircle },
                          { id: 'cloud', label: 'Cloud Infrastructure Hardening', icon: Building2 },
                        ].map((concern) => (
                          <button
                            key={concern.id}
                            type="button"
                            onClick={() => updateFormData('securityConcern', concern.id)}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                              formData.securityConcern === concern.id
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            <concern.icon className="w-5 h-5" />
                            <span className="font-medium">{concern.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Timeline for Implementation</label>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { id: 'high', label: 'Immediate / Critical (Next 30 days)', icon: AlertCircle },
                          { id: 'medium', label: 'Strategic Planning (1-3 months)', icon: Clock },
                          { id: 'low', label: 'Long-term Roadmap (3+ months)', icon: ShieldAlert },
                        ].map((urgency) => (
                          <button
                            key={urgency.id}
                            type="button"
                            onClick={() => updateFormData('urgency', urgency.id)}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                              formData.urgency === urgency.id
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            <urgency.icon className="w-5 h-5" />
                            <span className="font-medium">{urgency.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 5 && (
                  <motion.div
                    key="step5"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="space-y-3">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Estimated Annual Security Budget</label>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { id: 'low', label: 'Under $25,000', icon: Building2 },
                          { id: 'medium', label: '$25,000 - $100,000', icon: Building2 },
                          { id: 'high', label: '$100,000+', icon: Building2 },
                        ].map((budget) => (
                          <button
                            key={budget.id}
                            type="button"
                            onClick={() => updateFormData('budget', budget.id)}
                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                              formData.budget === budget.id
                                ? 'bg-accent/10 border-accent text-accent'
                                : 'bg-white/5 border-white/10 text-muted-foreground hover:border-white/20'
                            }`}
                          >
                            <budget.icon className="w-5 h-5" />
                            <span className="font-medium">{budget.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex gap-4 pt-4">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    className="h-14 px-8 rounded-xl border-white/10 hover:bg-white/5"
                  >
                    Back
                  </Button>
                )}
                {step < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    disabled={
                      (step === 1 && (!formData.name || !formData.email)) ||
                      (step === 2 && (!formData.businessType || !formData.businessSize)) ||
                      (step === 3 && !formData.securityConcern) ||
                      (step === 4 && !formData.urgency)
                    }
                    className="flex-grow h-14 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold"
                  >
                    Continue <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.budget}
                    className="flex-grow h-14 rounded-xl bg-accent hover:bg-accent/90 text-background font-bold shadow-[0_0_30px_rgba(0,150,255,0.3)]"
                  >
                    {isSubmitting ? "Securing Connection..." : "Get My Free Assessment"}
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-8 pt-8 border-t border-white/5 space-y-6">
              <div className="flex items-center justify-center gap-6 text-[10px] text-muted-foreground uppercase tracking-[0.3em] font-bold">
                <span className="flex items-center gap-2"><ShieldAlert className="w-3 h-3 text-accent" /> Encrypted</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-accent" /> GDPR Compliant</span>
                <span className="flex items-center gap-2"><User className="w-3 h-3 text-accent" /> Confidential</span>
              </div>
              <p className="text-center text-[10px] text-muted-foreground/60 font-medium leading-relaxed max-w-xs mx-auto">
                Your data is protected by military-grade encryption. We never share your information with third parties.
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-[2.5rem] p-16 text-center space-y-8 border-accent"
          >
            <div className="w-24 h-24 rounded-full bg-accent flex items-center justify-center text-background mx-auto shadow-[0_0_50px_rgba(0,150,255,0.5)]">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <div className="space-y-4">
              <h3 className="text-4xl font-display font-bold tracking-tight">Assessment Initiated</h3>
              <p className="text-xl text-muted-foreground font-light max-w-md mx-auto">
                Our intelligence team is reviewing your profile. A senior consultant will reach out within 4 business hours.
              </p>
            </div>
            <div className="p-6 bg-accent/5 rounded-2xl border border-accent/20 inline-block">
              <p className="text-accent font-bold flex items-center gap-2">
                <Clock className="w-5 h-5" /> Priority Queue: Active
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
